import { getColorSqDistance } from '@workwave-tidal/core/javascript-utils/color'
import { useMemo } from 'react'
import type { PaletteColorDistance, ThemeColorEntry } from '../types.js'
import { useParseTheme } from './useParseTheme.js'

type Params = {
  limit: number
  color: string
  includeContrastText: boolean
}

export function useGetNearestColors(params: Params) {
  const { color, includeContrastText, limit } = params

  const paletteColors = useParseTheme()

  return useMemo(
    () => getNearestColors(paletteColors, color, includeContrastText, limit),
    [paletteColors, color, includeContrastText, limit],
  )
}

function getNearestColors(colors: ThemeColorEntry[], color: string, includeContrastText: boolean, limit: number) {
  return colors
    .reduce<PaletteColorDistance[]>((acc, entry) => {
      if (!includeContrastText && entry.path.includes('contrastText')) return acc

      acc.push({
        ...entry,
        dist: getColorSqDistance(color, entry.color),
      })

      return acc
    }, [])
    .toSorted(sortPaletteColorMatches)
    .slice(0, limit)
}

/**
 * Sort colors by square distance, shorter is better
 * Fallback to color name length for equidistant colors
 * e.g. "primary dark" wins over "tidalColors oceanBlue 700"
 */
function sortPaletteColorMatches(a: PaletteColorDistance, b: PaletteColorDistance) {
  const diff = a.dist - b.dist

  if (diff === 0) {
    // assume shorter names are a better match for equidistant colors
    return a.path.length - b.path.length
  }

  return diff
}
